/**
 * Manual Testing Coverage Reporter
 * Uses COOKIES for session persistence (production-ready)
 * Sends window.__coverage__ to the manual-testing-service backend.
 */
declare global {
  interface Window {
    __coverage__: any;
    __coverage_session__: string | null;
  }
}

// Configuration
const SEND_INTERVAL = 5000; // Send coverage every 5 seconds
const INTERACTION_RESET_FALLBACK_MS = 30_000; // start flush even if user never interacts
const COOKIE_NAME = '_coverage_session';
const COOKIE_TESTER = '_coverage_tester';
const COOKIE_FEATURE = '_coverage_feature';
const COOKIE_BACKEND = '_coverage_backend'; // <-- New cookie for backend URL
const COOKIE_MAX_AGE = 86400; // 24 hours
const SESSION_ACTIVE_KEY = '_coverage_session_active';

// Cookie utilities
function setCookie(name: string, value: string, maxAge: number = COOKIE_MAX_AGE): void {
  document.cookie = `${name}=${encodeURIComponent(value)};path=/;max-age=${maxAge};SameSite=Lax`;
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

function deleteCookie(name: string): void {
  document.cookie = `${name}=;path=/;max-age=0`;
}

/** Zero Istanbul counters in the browser (excludes app-load/bootstrap noise). */
function resetFrontendCoverage(): void {
  const coverage = window.__coverage__;
  if (!coverage) return;

  for (const file of Object.keys(coverage)) {
    const fc = coverage[file];
    if (fc?.s) for (const k of Object.keys(fc.s)) fc.s[k] = 0;
    if (fc?.f) for (const k of Object.keys(fc.f)) fc.f[k] = 0;
    if (fc?.b) {
      for (const k of Object.keys(fc.b)) {
        fc.b[k] = fc.b[k].map(() => 0);
      }
    }
    if (fc?.l) for (const k of Object.keys(fc.l)) fc.l[k] = 0;
  }
}

class ManualTestingCoverageReporter {
  private sessionId: string | null = null;
  private intervalId: any = null;
  private baselineResetCleanup: (() => void) | null = null; 
  private sending: boolean = false;
  private testerName: string | null = null;
  private featureName: string | null = null;
  private backendUrl: string | null = null; // <-- Added to class state

  constructor() {
    // Priority: 1. Hash fragment (new session from launch URL), 2. Cookie (existing session)
    if (!this.restoreFromHash()) {
      this.restoreFromCookie();
    }
  }

  /**
   * Restore session from URL hash fragment (from the launch URL)
   * Hash format: #_cs=sessionId&_tn=testerName&_fn=featureName&_bu=backendUrl
   */
  private restoreFromHash(): boolean {
    const hash = window.location.hash;
    if (!hash) return false;

    const sessionMatch = hash.match(/_cs=([^&]+)/);
    if (!sessionMatch) return false;

    // Extract all parameters
    const backendMatch = hash.match(/_bu=([^&]+)/);
    const backendUrl = backendMatch ? decodeURIComponent(backendMatch[1]) : null;
    const sessionId = decodeURIComponent(sessionMatch[1]);
    const testerMatch = hash.match(/_tn=([^&]+)/);
    const featureMatch = hash.match(/_fn=([^&]+)/);

    const testerName = testerMatch ? decodeURIComponent(testerMatch[1]) : null;
    const featureName = featureMatch ? decodeURIComponent(featureMatch[1]) : null;

    // Clear hash from URL (clean URL)
    history.replaceState(null, '', window.location.pathname + window.location.search);

    console.log('[Coverage] Session from tracking URL:', sessionId);

    // Only start if we successfully extracted a backend URL
    if (backendUrl) {
      this.startInternal(sessionId, testerName, featureName, backendUrl, true);
      return true;
    }

    return false;
  }

  /**
   * Restore session from cookie (for page navigation persistence)
   */
  private restoreFromCookie(): void {
    const savedSession = getCookie(COOKIE_NAME);
    const savedBackend = getCookie(COOKIE_BACKEND); // <-- Load saved backend
    const activeSession = sessionStorage.getItem(SESSION_ACTIVE_KEY);

    // Only resume in the same tab that started the session (avoids stale cookies on app reload)
    if (savedSession && savedBackend && activeSession === savedSession) {
      const savedTester = getCookie(COOKIE_TESTER);
      const savedFeature = getCookie(COOKIE_FEATURE);
      console.log('[Coverage] Restoring session from cookie:', savedSession);
      this.startInternal(savedSession, savedTester, savedFeature, savedBackend, false);
    }
  }

  /**
   * Start a manual testing session manually
   */
  start(sessionId: string, backendUrl: string, testerName?: string, featureName?: string): void {
    this.startInternal(sessionId, testerName, featureName, backendUrl, true);
  }

  /** Cancel a pending deferred baseline reset (new session / stop). */
  private cancelBaselineReset(): void {
    if (this.baselineResetCleanup) {
      this.baselineResetCleanup();
      this.baselineResetCleanup = null;
    }
  }

  /**
   * Wait for first user interaction, then zero bootstrap noise and start flushing.
   * Bootstrap/lazy-load coverage before the first click is never sent to the backend.
   */
  private scheduleInteractionReset(): void {
    this.cancelBaselineReset();

    let done = false;
    let fallbackId: ReturnType<typeof setTimeout> | null = null;
    const interactionEvents = ['click', 'keydown', 'touchstart', 'pointerdown'] as const;

    const cleanup = () => {
      for (const type of interactionEvents) {
        document.removeEventListener(type, onInteraction, true);
      }
      window.removeEventListener('popstate', onInteraction);
      window.removeEventListener('hashchange', onInteraction);
      if (fallbackId !== null) {
        clearTimeout(fallbackId);
        fallbackId = null;
      }
    };

    const runReset = (reason: string) => {
      if (done) return;
      done = true;
      cleanup();
      this.baselineResetCleanup = null;

      resetFrontendCoverage();
      console.log(`[Coverage] Baseline reset on ${reason}`);

      void this.send();

      if (!this.intervalId) {
        this.intervalId = setInterval(() => this.send(), SEND_INTERVAL);
      }
    };

    const onInteraction = () => runReset('user interaction');

    for (const type of interactionEvents) {
      document.addEventListener(type, onInteraction, { capture: true });
    }
    window.addEventListener('popstate', onInteraction);
    window.addEventListener('hashchange', onInteraction);

    fallbackId = setTimeout(
      () => runReset('fallback timeout'),
      INTERACTION_RESET_FALLBACK_MS,
    );

    this.baselineResetCleanup = cleanup;
  }

  /**
   * Internal logic to start the session, save state, and set cookies
   */
  private startInternal(
    sessionId: string,
    testerName: string | null | undefined,
    featureName: string | null | undefined,
    backendUrl: string, // <-- Added backendUrl to signature
    notifyBackend: boolean = true
  ): void {
    // Stop existing session if any
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    this.cancelBaselineReset();

    // Save to class state
    this.sessionId = sessionId;
    this.testerName = testerName || null;
    this.featureName = featureName || null;
    this.backendUrl = backendUrl; // <-- Save backendUrl to state
    window.__coverage_session__ = sessionId;

    // Save to cookies for persistence
    setCookie(COOKIE_NAME, sessionId);
    setCookie(COOKIE_BACKEND, backendUrl); // <-- Save backend URL to cookie
    if (testerName) setCookie(COOKIE_TESTER, testerName);
    if (featureName) setCookie(COOKIE_FEATURE, featureName);

    try {
      sessionStorage.setItem(SESSION_ACTIVE_KEY, sessionId);
    } catch {
      /* sessionStorage blocked */
    }
    
    // Fresh session (launch URL or coverage.start): clear load-time counters.
    // Cookie restore (same-tab navigation): do NOT reset — keep accumulated coverage.
    // Stop any pending reset from a prior session
    this.cancelBaselineReset();

    // Fresh session: reset on first interaction, then start flushing.
    // Cookie restore: keep accumulated coverage across in-tab navigation.
    if (notifyBackend) {
      this.scheduleInteractionReset();
    } else {
      this.intervalId = setInterval(() => this.send(), SEND_INTERVAL);
    }

    console.log('[Coverage] Session started:', sessionId);
    console.log('[Coverage] Tester:', testerName || 'N/A');
    console.log('[Coverage] Feature:', featureName || 'N/A');
    console.log('[Coverage] Backend:', backendUrl);
  }

  async send(): Promise<void> {
    // Failsafe: Don't send if we don't have a backend URL
    if (!this.sessionId || !window.__coverage__ || !this.backendUrl || this.sending) {
      return;
    }

    this.sending = true;
    try {
      // Sends the lightweight coverage to the backend
      const response = await fetch(`${this.backendUrl}/api/manual-testing/sessions/flush`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: this.sessionId,
          coverage:  window.__coverage__
        })
      });

      if (!response.ok) {
        console.warn('[Coverage] Send failed:', response.status);
        if (response.status === 401 || response.status === 403) {
          this.stopPolling();
        }
      }
    } catch (e) {
      console.warn('[Coverage] Send failed:', e);
    } finally {
      this.sending = false;
    }
  }


  private stopPolling(): void {
    this.cancelBaselineReset(); 
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    try {
      sessionStorage.removeItem(SESSION_ACTIVE_KEY);
    } catch {
      /* sessionStorage blocked */
    }
  }

  async stop(): Promise<void> {
    this.stopPolling();

    // Send final coverage
    await this.send();

    // Notify backend
    if (this.sessionId && this.backendUrl) {
      try {
        // Use dynamic backend URL here instead of static V1_BACKEND_URL
        await fetch(`${this.backendUrl}/api/manual-testing/sessions/${this.sessionId}/end`, {
          method: 'POST'
        });
        console.log('[Coverage] Session ended:', this.sessionId);
      } catch (e) {
        console.warn('[Coverage] Failed to notify end:', e);
      }
    }

    // Clear all cookies
    deleteCookie(COOKIE_NAME);
    deleteCookie(COOKIE_TESTER);
    deleteCookie(COOKIE_FEATURE);
    deleteCookie(COOKIE_BACKEND); // <-- Clear backend cookie

    // Clear state
    this.sessionId = null;
    this.testerName = null;
    this.featureName = null;
    this.backendUrl = null;
    window.__coverage_session__ = null;
  }

  getSessionId(): string | null {
    return this.sessionId;
  }

  getTesterName(): string | null {
    return this.testerName;
  }

  getFeatureName(): string | null {
    return this.featureName;
  }

  isActive(): boolean {
    return this.sessionId !== null;
  }

  /**
   * Get session status for debugging
   */
  status(): object {
    return {
      active: this.isActive(),
      sessionId: this.sessionId,
      testerName: this.testerName,
      featureName: this.featureName,
      backendUrl: this.backendUrl // <-- Return dynamic backend URL
    };
  }
}

// Create singleton instance
export const manualTestingCoverage = new ManualTestingCoverageReporter();

// Expose globally for easy access from browser console
(window as any).coverage = {
  start: (sessionId: string, backendUrl: string, testerName?: string, featureName?: string) =>
    manualTestingCoverage.start(sessionId, backendUrl, testerName, featureName),
  stop: () => manualTestingCoverage.stop(),
  status: () => manualTestingCoverage.status(),
  isActive: () => manualTestingCoverage.isActive(),
  reset: () => {
    resetFrontendCoverage();
    void manualTestingCoverage.send();
    console.log('[Coverage] Frontend counters reset manually');
  },
};

// Log instructions on load
console.log('[Coverage] Manual Testing Coverage Reporter loaded');
console.log('[Coverage] To start: coverage.start("session-id", "backend-url", "tester-name", "feature-name")');
console.log('[Coverage] To stop:  coverage.stop()');
console.log('[Coverage] Status:   coverage.status()');
console.log('[Coverage] To reset:  coverage.reset()');